import { useState, useEffect } from "react";
import { MainLayout } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Brain,
  Play,
  Square,
  RefreshCw,
  FileText,
  Image,
  TrendingUp,
  Globe,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  BarChart3,
  Sparkles,
  LineChart as LineChartIcon,
  Mail,
  Pencil,
  Save,
  X,
  Trash2,
  Plus,
  ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API_URL = window.location.origin;

const SECTOR_ICONS = {
  estetica: "💆",
  parrucchieri: "💇",
  farmacie: "💊",
  retail: "🛍️",
  centri_benessere: "🧘",
};

export default function AIContentEnginePage() {
  const [status, setStatus] = useState(null);
  const [sectors, setSectors] = useState({});
  const [countries, setCountries] = useState({});
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    content: "",
    meta_description: "",
    header_image: "",
    gallery_images: [],
    keywords: [],
    categoria_display: "",
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Trend analysis state
  const [trendData, setTrendData] = useState(null);
  const [customKeywordsText, setCustomKeywordsText] = useState("");
  const [loadingCustomKW, setLoadingCustomKW] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [showTrendDialog, setShowTrendDialog] = useState(false);
  
  // Form state
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("IT");
  const [generateImages, setGenerateImages] = useState(true);
  
  // Custom topic state
  const [customTopic, setCustomTopic] = useState("");
  const [generatingCustom, setGeneratingCustom] = useState(false);
  
  // Newsletter state
  const [sendingNewsletter, setSendingNewsletter] = useState(null); // article_id being sent

  // Automatic article generation state
  const [generatingAuto, setGeneratingAuto] = useState(false);
  const [scheduleDays, setScheduleDays] = useState(1);
  const [articlesPerDay, setArticlesPerDay] = useState(2);
  const [rotationStatus, setRotationStatus] = useState(null);

  // Colors for charts
  const CHART_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088fe"];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Load status
      const statusRes = await fetch(`${API_URL}/api/ai/status`, { headers });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
        setGenerating(statusData.is_running);
      }

      // Load sectors
      const sectorsRes = await fetch(`${API_URL}/api/ai/sectors`, { headers });
      if (sectorsRes.ok) {
        const sectorsData = await sectorsRes.json();
        setSectors(sectorsData.sectors || {});
        setCountries(sectorsData.countries || {});
      }

      // Load articles
      const articlesRes = await fetch(`${API_URL}/api/ai/articles?limit=20`, { headers });
      if (articlesRes.ok) {
        const articlesData = await articlesRes.json();
        setArticles(articlesData.articles || []);
      }

      // Load stats
      const statsRes = await fetch(`${API_URL}/api/ai/stats`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load rotation status
      const rotationRes = await fetch(`${API_URL}/api/admin/article-rotation-status`, { headers });
      if (rotationRes.ok) {
        const rotationData = await rotationRes.json();
        setRotationStatus(rotationData);
      }
    } catch (error) {
      console.error("Errore caricamento dati:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const generateSingleContent = async () => {
    if (!selectedSector) {
      toast.error("Seleziona un settore");
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/generate/single`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sector: selectedSector,
          country: selectedCountry,
          generate_image: generateImages,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Contenuto generato per ${sectors[selectedSector]}`);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella generazione");
      }
    } catch (error) {
      toast.error("Errore nella generazione");
    } finally {
      setGenerating(false);
    }
  };

  // Genera articolo su argomento personalizzato
  const generateCustomTopicArticle = async () => {
    if (!customTopic || customTopic.trim().length < 5) {
      toast.error("Inserisci un argomento (minimo 5 caratteri)");
      return;
    }

    setGeneratingCustom(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/generate/custom-topic`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          topic: customTopic.trim(),
          country: selectedCountry,
          generate_image: false,
          language: "it",
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Articolo su "${customTopic}" generato con successo!`);
        setCustomTopic("");
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella generazione");
      }
    } catch (error) {
      toast.error("Errore nella generazione dell'articolo");
    } finally {
      setGeneratingCustom(false);
    }
  };

  // Genera articoli automatici (1, 2, o 3)
  const generateAutoArticles = async (numArticles) => {
    setGeneratingAuto(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/generate-articles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ num_articles: numArticles }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        // Aggiorna i dati dopo qualche secondo
        setTimeout(() => loadData(), 5000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella generazione");
      }
    } catch (error) {
      toast.error("Errore nella generazione degli articoli");
    } finally {
      setGeneratingAuto(false);
    }
  };

  // Pianifica generazione articoli per più giorni
  const scheduleArticles = async () => {
    if (scheduleDays < 1 || scheduleDays > 30) {
      toast.error("Seleziona un numero di giorni tra 1 e 30");
      return;
    }

    setGeneratingAuto(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/schedule-articles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          days: scheduleDays,
          articles_per_day: articlesPerDay
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        // Aggiorna i dati dopo qualche secondo
        setTimeout(() => loadData(), 5000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella pianificazione");
      }
    } catch (error) {
      toast.error("Errore nella pianificazione degli articoli");
    } finally {
      setGeneratingAuto(false);
    }
  };

  // Invia articolo via newsletter
  const sendArticleNewsletter = async (articleId) => {
    setSendingNewsletter(articleId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/articles/${articleId}/send-newsletter`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ article_id: articleId }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Newsletter inviata!");
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore nell'invio della newsletter");
    } finally {
      setSendingNewsletter(null);
    }
  };

  const startDailyGeneration = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/generate/daily`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country: selectedCountry,
          articles_per_sector: 1,
          generate_images: generateImages,
        }),
      });

      if (response.ok) {
        toast.success("Generazione giornaliera avviata in background");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'avvio");
      }
    } catch (error) {
      toast.error("Errore nell'avvio della generazione");
    }
  };

  const analyzeTrends = async (sector) => {
    if (!sector) {
      toast.error("Seleziona un settore");
      return;
    }
    
    setLoadingTrends(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/ai/trends/${sector}?country=${selectedCountry}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTrendData(data);
        setShowTrendDialog(true);
        toast.success(`Trend analizzati per ${sectors[sector]}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'analisi");
      }
    } catch (error) {
      toast.error("Errore nell'analisi dei trend");
    } finally {
      setLoadingTrends(false);
    }
  };

  const analyzeCustomKeywords = async () => {
    const list = customKeywordsText
      .split(/[\n,;]/)
      .map((k) => k.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Scrivi almeno una parola chiave da analizzare");
      return;
    }
    setLoadingCustomKW(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/trends/custom`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ keywords: list, country: selectedCountry }),
      });
      if (response.ok) {
        const data = await response.json();
        setTrendData({
          top_trends: [],
          google_trends_raw: data.google_trends_raw,
          volume_estimates: data.volume_estimates,
          custom_analysis: true,
        });
        setShowTrendDialog(true);
        toast.success(`Analizzate ${list.length} keyword`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore analisi keyword");
      }
    } catch (e) {
      toast.error("Errore nella richiesta");
    } finally {
      setLoadingCustomKW(false);
    }
  };


  const stopGeneration = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/ai/stop`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Richiesta di interruzione inviata");
      setGenerating(false);
    } catch (error) {
      toast.error("Errore nell'interruzione");
    }
  };

  const viewArticle = async (articleId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const article = await response.json();
        setSelectedArticle(article);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dell'articolo");
    }
  };

  const updateArticleStatus = async (articleId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/ai/articles/${articleId}/status?status=${newStatus}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Stato aggiornato a ${newStatus}`);
      loadData();
      if (selectedArticle?.article_id === articleId) {
        setSelectedArticle({ ...selectedArticle, status: newStatus });
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const getStatusBadge = (articleStatus) => {
    const statusConfig = {
      draft: { color: "bg-yellow-500", label: "Bozza" },
      reviewed: { color: "bg-blue-500", label: "Revisionato" },
      published: { color: "bg-green-500", label: "Pubblicato" },
    };
    const config = statusConfig[articleStatus] || statusConfig.draft;
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  // ==================== EDIT FUNCTIONS ====================
  
  const startEditing = () => {
    if (!selectedArticle) return;
    setEditForm({
      title: selectedArticle.title || "",
      content: selectedArticle.content || "",
      meta_description: selectedArticle.meta_description || "",
      header_image: selectedArticle.header_image || "",
      gallery_images: selectedArticle.gallery_images || [],
      keywords: selectedArticle.keywords || [],
      categoria_display: selectedArticle.categoria_display || "",
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({
      title: "",
      content: "",
      meta_description: "",
      header_image: "",
      gallery_images: [],
      keywords: [],
      categoria_display: "",
    });
    setNewKeyword("");
    setNewImageUrl("");
  };

  const saveArticle = async () => {
    if (!selectedArticle) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/ai/articles/${selectedArticle.article_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success("Articolo salvato con successo!");
        
        // Aggiorna l'articolo selezionato e la lista
        setSelectedArticle(data.article);
        setArticles(articles.map(a => 
          a.article_id === selectedArticle.article_id ? data.article : a
        ));
        setIsEditing(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      console.error("Errore salvataggio:", error);
      toast.error("Errore nel salvataggio dell'articolo");
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async () => {
    if (!selectedArticle) return;
    
    if (!window.confirm("Sei sicuro di voler eliminare questo articolo? L'azione non può essere annullata.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/ai/articles/${selectedArticle.article_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success("Articolo eliminato");
        setSelectedArticle(null);
        setArticles(articles.filter(a => a.article_id !== selectedArticle.article_id));
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      console.error("Errore eliminazione:", error);
      toast.error("Errore nell'eliminazione dell'articolo");
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !editForm.keywords.includes(newKeyword.trim())) {
      setEditForm({
        ...editForm,
        keywords: [...editForm.keywords, newKeyword.trim()]
      });
      setNewKeyword("");
    }
  };

  const removeKeyword = (keyword) => {
    setEditForm({
      ...editForm,
      keywords: editForm.keywords.filter(k => k !== keyword)
    });
  };

  const addGalleryImage = () => {
    if (newImageUrl.trim() && !editForm.gallery_images.includes(newImageUrl.trim())) {
      setEditForm({
        ...editForm,
        gallery_images: [...editForm.gallery_images, newImageUrl.trim()]
      });
      setNewImageUrl("");
    }
  };

  const removeGalleryImage = (url) => {
    setEditForm({
      ...editForm,
      gallery_images: editForm.gallery_images.filter(u => u !== url)
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
            <Brain className="w-8 h-8 text-primary" />
            ODR AI Content Engine
          </h1>
          <p className="text-muted-foreground mt-1">
            Motore AI per generazione automatica di contenuti
          </p>
        </div>

        {/* Status Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Stato del Sistema
              </span>
              <Button variant="ghost" size="sm" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {stats?.total_articles || 0}
                </div>
                <div className="text-sm text-muted-foreground">Articoli Totali</div>
              </div>
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {stats?.by_status?.published || 0}
                </div>
                <div className="text-sm text-muted-foreground">Pubblicati</div>
              </div>
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {stats?.by_status?.draft || 0}
                </div>
                <div className="text-sm text-muted-foreground">Bozze</div>
              </div>
              <div className="text-center p-3 bg-secondary/30 rounded-lg">
                <div className={`text-2xl font-bold ${generating ? "text-green-600" : "text-gray-400"}`}>
                  {generating ? "Attivo" : "Inattivo"}
                </div>
                <div className="text-sm text-muted-foreground">Motore</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generation Controls */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Generazione Contenuti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Settore</label>
                <Select value={selectedSector} onValueChange={setSelectedSector}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(sectors).map(([key, name]) => (
                      <SelectItem key={key} value={key}>
                        {SECTOR_ICONS[key]} {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Paese</label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(countries).map(([code, info]) => (
                      <SelectItem key={code} value={code}>
                        {info.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Opzioni</label>
                <Button
                  variant={generateImages ? "default" : "outline"}
                  className="w-full"
                  onClick={() => setGenerateImages(!generateImages)}
                >
                  <Image className="w-4 h-4 mr-2" />
                  {generateImages ? "Immagini: SI" : "Immagini: NO"}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={generateSingleContent}
                disabled={generating || !selectedSector}
                className="flex-1 md:flex-none"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Genera Singolo
              </Button>
              
              <Button
                onClick={() => analyzeTrends(selectedSector)}
                disabled={loadingTrends || !selectedSector}
                variant="outline"
                className="flex-1 md:flex-none"
              >
                {loadingTrends ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <LineChartIcon className="w-4 h-4 mr-2" />
                )}
                Analizza Trend
              </Button>
              
              <Button
                onClick={startDailyGeneration}
                disabled={generating}
                variant="secondary"
                className="flex-1 md:flex-none"
              >
                <Play className="w-4 h-4 mr-2" />
                Genera Tutti i Settori
              </Button>
              
              {generating && (
                <Button onClick={stopGeneration} variant="destructive">
                  <Square className="w-4 h-4 mr-2" />
                  Ferma
                </Button>
              )}
            </div>
            
            {/* Custom Topic Section */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Argomento Libero Personalizzato
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Scrivi qualsiasi argomento e l'AI genererà un articolo scientifico dedicato
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  placeholder="Es: Benefici della camminata nella natura, Yoga per principianti, Alimentazione anti-stress..."
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={generatingCustom}
                />
                <Button
                  onClick={generateCustomTopicArticle}
                  disabled={generatingCustom || !customTopic.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {generatingCustom ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {generatingCustom ? "Generando..." : "Genera Articolo"}
                </Button>
              </div>
            </div>

            {/* ==== NUOVA SEZIONE: Ricerca Volume Keyword Libere ==== */}
            <div className="mt-6 pt-6 border-t border-blue-200 bg-blue-50/50 -mx-6 px-6 py-6 rounded-b-lg">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-800">
                🔎 Ricerca Volume Google — Keyword Libere
              </h3>
              <p className="text-sm text-blue-700 mb-1">
                <strong>Scrivi QUALSIASI parola</strong> che vuoi analizzare (una per riga o separata da virgola).
                Ti dirò quante persone la cercano su Google, dove, e cosa cercano di correlato.
              </p>
              <p className="text-xs text-blue-600 mb-4 italic">
                💚 Gratis · Nessun credito LLM · Solo dati Google Trends reali · Paese: {selectedCountry}
              </p>
              <textarea
                value={customKeywordsText}
                onChange={(e) => setCustomKeywordsText(e.target.value)}
                placeholder={"Esempi (uno per riga o separati da virgola):\ncosmetici oncologici\ncrema idratante viso\nprotezione solare pelle sensibile\nshampoo delicato\ndermatite atopica adulti"}
                rows={5}
                className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                disabled={loadingCustomKW}
                data-testid="custom-keywords-textarea"
              />
              <div className="flex flex-wrap gap-3 mt-3 items-center">
                <Button
                  onClick={analyzeCustomKeywords}
                  disabled={loadingCustomKW || !customKeywordsText.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="btn-analyze-custom-keywords"
                >
                  {loadingCustomKW ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizzo su Google Trends…</>
                  ) : (
                    <>📊 Analizza volume su Google</>
                  )}
                </Button>
                <span className="text-xs text-blue-700">
                  Max 40 keyword per analisi · Attesa 15-30 secondi
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generazione Automatica Articoli Benessere */}
        <Card className="mb-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Clock className="w-5 h-5" />
              Generazione Automatica Articoli Benessere
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Genera articoli automatici con testo AI (GPT-4o) e immagini AI (Gemini)
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Generation Buttons */}
            <div>
              <h4 className="font-medium mb-3 text-green-800">Generazione Rapida</h4>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => generateAutoArticles(1)}
                  disabled={generatingAuto}
                  variant="outline"
                  className="border-green-300 hover:bg-green-100"
                >
                  {generatingAuto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  1 Articolo
                </Button>
                <Button
                  onClick={() => generateAutoArticles(2)}
                  disabled={generatingAuto}
                  variant="outline"
                  className="border-green-300 hover:bg-green-100"
                >
                  {generatingAuto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  2 Articoli
                </Button>
                <Button
                  onClick={() => generateAutoArticles(3)}
                  disabled={generatingAuto}
                  variant="outline"
                  className="border-green-300 hover:bg-green-100"
                >
                  {generatingAuto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  3 Articoli
                </Button>
              </div>
              {generatingAuto && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generazione in corso... Ogni articolo richiede circa 30-40 secondi.
                </p>
              )}
            </div>

            {/* Schedule Generation */}
            <div className="pt-4 border-t border-green-200">
              <h4 className="font-medium mb-3 text-green-800">Pianificazione Avanzata</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Genera articoli per più giorni in anticipo (tutti subito)
              </p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Giorni (1-30)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={scheduleDays}
                    onChange={(e) => setScheduleDays(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-24 px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Articoli/Giorno</label>
                  <Select 
                    value={articlesPerDay.toString()} 
                    onValueChange={(v) => setArticlesPerDay(parseInt(v))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={scheduleArticles}
                  disabled={generatingAuto}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {generatingAuto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Genera {scheduleDays * articlesPerDay} Articoli
                </Button>
              </div>
            </div>

            {/* Rotation Status */}
            {rotationStatus && (
              <div className="pt-4 border-t border-green-200">
                <h4 className="font-medium mb-3 text-green-800">Prossimi Argomenti in Rotazione</h4>
                <div className="bg-white/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Scheduler automatico: {rotationStatus.next_scheduled}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {rotationStatus.next_topics?.map((topic, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-2 text-sm p-2 bg-green-50 rounded"
                      >
                        <span className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center text-xs font-medium">
                          {topic.position}
                        </span>
                        <span className="flex-1 truncate">{topic.topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sectors Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Settori Monitorati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(sectors).map(([key, name]) => (
                <div
                  key={key}
                  className="p-4 bg-secondary/30 rounded-lg text-center hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedSector(key)}
                >
                  <div className="text-3xl mb-2">{SECTOR_ICONS[key]}</div>
                  <div className="font-medium text-sm">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stats?.by_sector?.[key] || 0} articoli
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Articles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Articoli Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {articles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun articolo generato ancora</p>
                <p className="text-sm">Usa i controlli sopra per generare contenuti</p>
              </div>
            ) : (
              <div className="space-y-3">
                {articles.map((article) => (
                  <div
                    key={article.article_id}
                    className="p-4 border rounded-lg hover:bg-secondary/20 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{SECTOR_ICONS[article.sector]}</span>
                          <h3 className="font-semibold">{article.title}</h3>
                          {getStatusBadge(article.status)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span>{sectors[article.sector]}</span>
                          <span className="mx-2">•</span>
                          <span>{article.word_count} parole</span>
                          <span className="mx-2">•</span>
                          <span>{new Date(article.created_at).toLocaleDateString("it-IT")}</span>
                        </div>
                        {article.keywords?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {article.keywords.slice(0, 3).map((kw, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {kw}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Pulsante Pubblica/Bozza */}
                        <Button
                          variant={article.status === "published" ? "outline" : "default"}
                          size="sm"
                          onClick={() => updateArticleStatus(article.article_id, article.status === "published" ? "draft" : "published")}
                        >
                          {article.status === "published" ? "Nascondi" : "Pubblica"}
                        </Button>
                        
                        {/* Pulsante Invia Newsletter (solo se pubblicato) */}
                        {article.status === "published" && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => sendArticleNewsletter(article.article_id)}
                            disabled={sendingNewsletter === article.article_id || article.newsletter_sent}
                            className={article.newsletter_sent ? "bg-green-100 text-green-700" : ""}
                          >
                            {sendingNewsletter === article.article_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : article.newsletter_sent ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Inviato
                              </>
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-1" />
                                Newsletter
                              </>
                            )}
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewArticle(article.article_id)}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Modifica
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Article Detail Dialog */}
        <Dialog open={!!selectedArticle} onOpenChange={() => { setSelectedArticle(null); cancelEditing(); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="truncate pr-4">
                  {isEditing ? "Modifica Articolo" : selectedArticle?.title}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isEditing ? (
                    <>
                      <Button size="sm" variant="outline" onClick={startEditing}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button size="sm" variant="destructive" onClick={deleteArticle}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={cancelEditing}>
                        <X className="w-4 h-4 mr-1" />
                        Annulla
                      </Button>
                      <Button size="sm" onClick={saveArticle} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Salva
                      </Button>
                    </>
                  )}
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {selectedArticle && !isEditing && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(selectedArticle.status)}
                  <Badge variant="outline">{sectors[selectedArticle.sector]}</Badge>
                  <Badge variant="outline">{selectedArticle.word_count} parole</Badge>
                  {selectedArticle.categoria_display && (
                    <Badge variant="secondary">{selectedArticle.categoria_display}</Badge>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={selectedArticle.status === "draft" ? "default" : "outline"}
                    onClick={() => updateArticleStatus(selectedArticle.article_id, "draft")}
                  >
                    Bozza
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedArticle.status === "reviewed" ? "default" : "outline"}
                    onClick={() => updateArticleStatus(selectedArticle.article_id, "reviewed")}
                  >
                    Revisionato
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedArticle.status === "published" ? "default" : "outline"}
                    onClick={() => updateArticleStatus(selectedArticle.article_id, "published")}
                  >
                    Pubblicato
                  </Button>
                </div>

                {/* Header Image Preview */}
                {selectedArticle.header_image && (
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={selectedArticle.header_image} 
                      alt="Header" 
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                {/* Gallery Images */}
                {selectedArticle.gallery_images?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Galleria Immagini</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedArticle.gallery_images.map((img, i) => (
                        <img 
                          key={i}
                          src={img} 
                          alt={`Gallery ${i+1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="prose prose-sm max-w-none">
                  <div
                    className="whitespace-pre-wrap bg-secondary/20 p-4 rounded-lg text-sm max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{
                      __html: selectedArticle.content?.replace(/\n/g, "<br />"),
                    }}
                  />
                </div>

                {selectedArticle.keywords?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Keywords SEO</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.keywords.map((kw, i) => (
                        <Badge key={i} variant="secondary">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedArticle.scientific_sources?.sources?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Fonti Scientifiche</h4>
                    <ul className="text-sm space-y-1">
                      {selectedArticle.scientific_sources.sources.map((src, i) => (
                        <li key={i} className="text-muted-foreground">
                          • {src.title} ({src.source_type}, {src.year})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* EDIT MODE */}
            {selectedArticle && isEditing && (
              <Tabs defaultValue="content" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="content">Contenuto</TabsTrigger>
                  <TabsTrigger value="images">Immagini</TabsTrigger>
                  <TabsTrigger value="seo">SEO & Meta</TabsTrigger>
                </TabsList>
                
                {/* TAB CONTENUTO */}
                <TabsContent value="content" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Titolo</Label>
                    <Input
                      id="edit-title"
                      value={editForm.title}
                      onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                      placeholder="Titolo dell'articolo"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit-categoria">Categoria Display</Label>
                    <Input
                      id="edit-categoria"
                      value={editForm.categoria_display}
                      onChange={(e) => setEditForm({...editForm, categoria_display: e.target.value})}
                      placeholder="Es: Neuroscienze e Meditazione"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit-content">Contenuto (Markdown)</Label>
                    <Textarea
                      id="edit-content"
                      value={editForm.content}
                      onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                      placeholder="Scrivi il contenuto in formato Markdown..."
                      className="min-h-[400px] font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Supporta: ## Titoli, **grassetto**, *corsivo*, - liste, ![alt](url) per immagini
                    </p>
                  </div>
                </TabsContent>
                
                {/* TAB IMMAGINI */}
                <TabsContent value="images" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Immagine Header</Label>
                    <div className="flex gap-2">
                      <Input
                        value={editForm.header_image}
                        onChange={(e) => setEditForm({...editForm, header_image: e.target.value})}
                        placeholder="URL dell'immagine header"
                        className="flex-1"
                      />
                    </div>
                    {editForm.header_image && (
                      <div className="mt-2 rounded-lg overflow-hidden border">
                        <img 
                          src={editForm.header_image} 
                          alt="Header Preview" 
                          className="w-full h-40 object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Galleria Immagini</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        placeholder="URL nuova immagine"
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGalleryImage())}
                      />
                      <Button type="button" onClick={addGalleryImage} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {editForm.gallery_images.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                        {editForm.gallery_images.map((url, i) => (
                          <div key={i} className="relative group">
                            <img 
                              src={url} 
                              alt={`Gallery ${i+1}`}
                              className="w-full h-28 object-cover rounded-lg border"
                              onError={(e) => e.target.src = 'https://via.placeholder.com/200x150?text=Errore'}
                            />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(url)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Puoi aggiungere immagini nel contenuto usando la sintassi: ![descrizione](url)
                    </p>
                  </div>
                </TabsContent>
                
                {/* TAB SEO */}
                <TabsContent value="seo" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-meta">Meta Description</Label>
                    <Textarea
                      id="edit-meta"
                      value={editForm.meta_description}
                      onChange={(e) => setEditForm({...editForm, meta_description: e.target.value})}
                      placeholder="Descrizione per i motori di ricerca (max 160 caratteri)"
                      className="min-h-[80px]"
                      maxLength={160}
                    />
                    <p className="text-xs text-muted-foreground">
                      {editForm.meta_description.length}/160 caratteri
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Keywords SEO</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        placeholder="Aggiungi keyword"
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      />
                      <Button type="button" onClick={addKeyword} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {editForm.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {editForm.keywords.map((kw, i) => (
                          <Badge 
                            key={i} 
                            variant="secondary"
                            className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                            onClick={() => removeKeyword(kw)}
                          >
                            {kw} <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Trend Analysis Dialog with Charts */}
        <Dialog open={showTrendDialog} onOpenChange={setShowTrendDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LineChartIcon className="w-5 h-5" />
                Analisi Trend: {trendData?.sector_name} - {trendData?.country_name}
              </DialogTitle>
            </DialogHeader>
            
            {trendData && (
              <div className="space-y-6">
                {/* Volume Estimates Bar Chart */}
                {trendData.volume_estimates?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Stima Volume Ricerche Mensili
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={trendData.volume_estimates}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="keyword" 
                              tick={{ fontSize: 10 }}
                              interval={0}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis />
                            <Tooltip 
                              formatter={(value) => [`${value.toLocaleString()} ricerche/mese`, "Volume stimato"]}
                            />
                            <Bar dataKey="estimated_monthly_volume" fill="#8884d8" name="Volume Mensile">
                              {trendData.volume_estimates.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Trend Score Comparison */}
                {trendData.google_trends_raw?.trends_data?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Google Trends Score (0-100)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={trendData.google_trends_raw.trends_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="keyword" 
                              tick={{ fontSize: 10 }}
                              interval={0}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="current_interest" fill="#82ca9d" name="Interesse Attuale" />
                            <Bar dataKey="average_interest" fill="#8884d8" name="Media 3 Mesi" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Rate limit warning */}
                {trendData.google_trends_raw?.rate_limited && (
                  <Card className="border-red-300 bg-red-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-red-800 flex items-center gap-2">
                        🚦 Google Trends ha bloccato la richiesta
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-red-700">
                        {trendData.google_trends_raw.rate_limit_message || "Rate-limit HTTP 429."}
                      </p>
                      <p className="text-xs text-red-600 mt-2">
                        💡 <strong>Cosa fare</strong>: aspetta 5-10 minuti e riprova. Google Trends blocca gli IP con troppe richieste ravvicinate.
                        Puoi anche provare a lanciare meno keyword per volta (es. 3-5 alla volta invece di tutte insieme).
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Keyword senza dati Google Trends (troppo di nicchia) */}
                {trendData.google_trends_raw?.no_data_keywords?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                        ⚠️ Keyword troppo di nicchia per Google Trends
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-amber-700 mb-3">
                        Google Trends non ha volume di ricerca sufficiente per queste parole (sotto la soglia minima misurabile).
                        Significa che <strong>vengono cercate pochissime volte al mese</strong> ma sono comunque parole strategiche per SEO nicchia.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {trendData.google_trends_raw.no_data_keywords.map((kw, i) => (
                          <span key={i} className="inline-block bg-white border border-amber-300 text-amber-900 text-xs px-2 py-1 rounded-full">
                            {kw}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-amber-600 mt-3 italic">
                        💡 Consiglio: usa queste parole nei contenuti blog/articoli scientifici per posizionarti con bassa concorrenza.
                        Per parole con volume misurabile, prova sinonimi più generici (es. "cosmetici pelle sensibile" invece di "cosmetici post chemioterapia").
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Top Trends List */}
                {trendData.top_trends?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Top Trend Identificati</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {trendData.top_trends.map((trend, i) => (
                          <div key={i} className="p-3 bg-secondary/30 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{trend.topic}</h4>
                                <p className="text-sm text-muted-foreground">{trend.description}</p>
                              </div>
                              <div className="text-right">
                                <Badge className={
                                  trend.growth === "in crescita" ? "bg-green-500" :
                                  trend.growth === "stabile" ? "bg-yellow-500" : "bg-red-500"
                                }>
                                  {trend.growth === "in crescita" ? "↗️" : trend.growth === "stabile" ? "→" : "↘️"} {trend.growth}
                                </Badge>
                                <div className="text-sm mt-1">
                                  <span className="font-bold">{trend.trend_score}/100</span>
                                </div>
                                {trend.estimated_monthly_searches && (
                                  <div className="text-xs text-muted-foreground">
                                    ~{trend.estimated_monthly_searches.toLocaleString()} ricerche/mese
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* User Questions */}
                {trendData.user_questions?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Domande degli Utenti</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {trendData.user_questions.map((q, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-primary">❓</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Emerging Topics */}
                {trendData.emerging_topics?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Argomenti Emergenti</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {trendData.emerging_topics.map((topic, i) => (
                          <Badge key={i} variant="outline" className="text-sm">
                            🔥 {topic}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommended Content */}
                {trendData.recommended_content?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Articoli Consigliati da Generare</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {trendData.recommended_content.map((content, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-green-500">💡</span>
                            <span>{content}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                <div className="text-xs text-muted-foreground text-center">
                  Analisi effettuata il {new Date(trendData.analyzed_at).toLocaleString("it-IT")}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
